// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Accounts and their roles (design doc §12.4). Administrators only.
 *
 * An account appears once its owner has signed in — as an engineer, unless the deployment
 * names it an administrator — so giving someone a role means asking them to sign in once
 * first. The role is changed in place and takes effect on that account's next request.
 * The server refuses to demote the last administrator, and the refusal is shown on the row.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/api/endpoints";
import type { Role } from "@/api/types";
import { ErrorBanner } from "@/components/ErrorBanner";

const ROLES: Role[] = ["ADMIN", "ENGINEER", "VISITOR"];

export function Users() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const users = useQuery({ queryKey: ["users"], queryFn: api.users.list });
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false });

  const setRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      api.users.setRole(userId, role),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["users"] }),
        // Your own role may be the one that changed: the rail follows it.
        queryClient.invalidateQueries({ queryKey: ["me"] }),
      ]),
  });

  return (
    <section>
      <h1>{t("users.title")}</h1>
      <p className="muted">{t("users.hint")}</p>
      <ErrorBanner error={users.error ?? setRole.error} />
      <table>
        <thead>
          <tr>
            <th>{t("users.email")}</th>
            <th>{t("users.name")}</th>
            <th>{t("users.role")}</th>
            <th>{t("users.lastLogin")}</th>
          </tr>
        </thead>
        <tbody>
          {(users.data ?? []).map((user) => (
            <tr key={user.user_id}>
              <td>
                {user.email}
                {user.user_id === me.data?.user_id && (
                  <span className="muted"> {t("users.you")}</span>
                )}
              </td>
              <td>{user.display_name ?? "—"}</td>
              <td>
                <select
                  aria-label={t("users.roleOf", { email: user.email })}
                  value={user.role}
                  disabled={setRole.isPending}
                  onChange={(event) =>
                    setRole.mutate({ userId: user.user_id, role: event.target.value as Role })
                  }
                >
                  {ROLES.map((role) => (
                    <option key={role} value={role}>
                      {t(`roles.${role}`)}
                    </option>
                  ))}
                </select>
              </td>
              <td>{new Date(user.last_login_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
